import type { AfterResponseHook } from "ky";
import ky from "ky";

import { hideLoader, showLoader } from "@/redux/actions/loader";
import store from "@/redux/store";

import type { ApiContext } from "../types";
import { authRequestsTyped } from "../(requests)/authTyped";
import { workflowTokenStore } from "../helpers/workflowTokenStore";

// store.dispatch is typed as Dispatch<AnyAction> but auth actions are thunks
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const dispatch: (action: any) => any = store.dispatch;

// --- Local (localStorage) token refresh ---

let localRefreshPromise: Promise<string> | null = null;

// Mirrors the workflow refresh pattern — uses authRequestsTyped.newIdToken (httpPublic2)
// so there is no dependency on the authenticated ky instances or redux/actions/auth.
function refreshLocalTokenOnce(): Promise<string> {
  if (!localRefreshPromise) {
    localRefreshPromise = authRequestsTyped
      .newIdToken({
        data: {
          id_token: localStorage.getItem("token") ?? undefined,
          refresh_token: localStorage.getItem("refreshToken") ?? undefined,
        },
      })
      .then((res) => {
        const { AccessToken, IdToken } = res.AuthenticationResult;

        store.dispatch({ payload: { AccessToken, IdToken }, type: "REFRESH_ACCESS_TOKEN" });

        return IdToken;
      })
      .finally(() => {
        localRefreshPromise = null;
      });
  }

  return localRefreshPromise;
}

export const localTokenRefreshHook: AfterResponseHook = async ({
  options,
  request,
  response,
  retryCount,
}) => {
  if (response.status !== 403 || retryCount > 0) return undefined;

  const context = options.context as ApiContext | undefined;

  // Mirror old refreshLocalStorageTokens: keep spinner on during the refresh+retry window.
  // loaderEndHook already fired (hideLoader) for the 403; re-show here so the spinner
  // stays visible while we refresh the token and replay the original request.
  dispatch(showLoader());

  try {
    const newToken = await refreshLocalTokenOnce();

    // Respect per-error-type allowRetry: false
    const errorData = await response
      .clone() // clone so ky can still populate error.data for beforeError hooks
      .json()
      .catch((): null => null);

    const errorType = (errorData as { error?: { type?: string } } | null)?.error?.type ?? "";
    const isRetryAllowed = context?.exceptionConfig?.[errorType]?.allowRetry !== false;

    if (!isRetryAllowed) {
      dispatch(hideLoader());

      return undefined;
    }

    const newHeaders = new Headers(request.headers);

    newHeaders.set("Authorization", newToken);

    // loaderEndHook fires for the retry response — no explicit hideLoader needed on success
    return ky.retry({
      code: "TOKEN_REFRESHED",
      request: new Request(request, { headers: newHeaders }),
    });
  } catch {
    // No logOut() here — mirrors old refreshLocalStorageTokens behavior, which
    // cleared isAlreadyFetchingAccessToken without dispatching any logout action.
    // Transient failures are absorbed by the retry config on newIdToken (limit:1)
    // before this catch is ever reached. If both attempts fail the 403 propagates
    // to the caller; on genuine refresh-token expiry the user encounters errors
    // and re-authenticates voluntarily rather than being hard-redirected.
    dispatch(hideLoader());

    return undefined;
  }
};

// --- Workflow token refresh ---

let workflowRefreshPromise: Promise<string> | null = null;

function refreshWorkflowTokenOnce(): Promise<string> {
  if (!workflowRefreshPromise) {
    const current = workflowTokenStore.get();

    // authRequestsTyped.newIdToken uses httpPublic2 (public.ts) internally —
    // no circular dep through the authenticated ky instances.
    workflowRefreshPromise = authRequestsTyped
      .newIdToken({ data: { id_token: current?.idToken, refresh_token: current?.refreshToken } })
      .then((res) => {
        const newIdToken = res.AuthenticationResult.IdToken;

        workflowTokenStore.set({
          idToken: newIdToken,
          refreshToken: current?.refreshToken ?? "",
        });

        return newIdToken;
      })
      .catch((err: unknown) => {
        workflowTokenStore.clear();

        return Promise.reject(err);
      })
      .finally(() => {
        workflowRefreshPromise = null;
      });
  }

  return workflowRefreshPromise;
}

export const workflowTokenRefreshHook: AfterResponseHook = async ({
  options,
  request,
  response,
  retryCount,
}) => {
  if (response.status !== 403 || retryCount > 0) return undefined;

  const context = options.context as ApiContext | undefined;

  // Same loader pattern as localTokenRefreshHook
  dispatch(showLoader());

  try {
    const newToken = await refreshWorkflowTokenOnce();

    // Respect per-error-type allowRetry: false
    const errorData = await response
      .clone()
      .json()
      .catch((): null => null);
    const errorType = (errorData as { error?: { type?: string } } | null)?.error?.type ?? "";
    const isRetryAllowed = context?.exceptionConfig?.[errorType]?.allowRetry !== false;

    if (!isRetryAllowed) {
      dispatch(hideLoader());

      return undefined;
    }

    const newHeaders = new Headers(request.headers);

    newHeaders.set("Authorization", newToken);

    // loaderEndHook fires for the retry response — no explicit hideLoader needed on success
    return ky.retry({
      code: "WORKFLOW_TOKEN_REFRESHED",
      request: new Request(request, { headers: newHeaders }),
    });
  } catch {
    // No logOut() here — mirrors old refreshGlobalTokens behavior, which only
    // cleared global.workflowAuthIdToken without dispatching any logout action.
    // workflowTokenStore.clear() is already called inside refreshWorkflowTokenOnce.
    // Same transient-failure rationale as localTokenRefreshHook above.
    dispatch(hideLoader());

    return undefined;
  }
};
